import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import { OneUI, type ModalHandle } from './oneUi';

/**
 * 공통 모달. 규칙은 ONE 플랫폼 공통 UI(one-ui, ./oneUi.ts)에 맡긴다.
 *
 *  · 입력 모달(기본): 배경을 눌러도 닫히지 않고(흔들기만), '취소'·Esc 에서
 *    적은 내용이 바뀌었으면 '저장하지 않고 닫을까요?' 를 묻는다.
 *  · 정보 모달(input={false}): 배경으로 닫힌다 — 단 누른 곳·뗀 곳이 둘 다 배경일 때만.
 *    모달 안에서 글자를 끌다 배경에서 손을 떼도 닫히지 않는다.
 *  · Esc 는 맨 위 모달 하나만, Tab 은 모달 안에서 순환, 닫히면 열기 전 포커스로(기획서 16장).
 *
 * '취소' 버튼은 <ModalCancel> 을 쓴다 — onClose 를 바로 부르면 바뀐 내용 확인을 건너뛴다.
 */
const ModalCloseContext = createContext<() => void>(() => {});

/** 모달 안의 '취소' 버튼. 바뀐 내용이 있으면 묻고 닫는다. */
export function ModalCancel({
  children,
  disabled,
  className = 'btn',
}: {
  children: ReactNode;
  disabled?: boolean;
  className?: string;
}) {
  const requestClose = useContext(ModalCloseContext);
  return (
    <button type="button" className={className} onClick={requestClose} disabled={disabled}>
      {children}
    </button>
  );
}

export function Modal({
  title,
  desc,
  onClose,
  children,
  footer,
  input = true,
  isDirty,
}: {
  title: string;
  desc?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** false 면 정보 모달(배경으로 닫힘, 닫을 때 묻지 않음) */
  input?: boolean;
  /** 입력칸 값 말고 따로 들고 있는 상태(고른 파일 등)로 '바뀜' 을 판정할 때 */
  isDirty?: () => boolean;
}) {
  const backRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<ModalHandle | null>(null);
  // 부모가 렌더할 때마다 새 함수가 와도 묶음을 다시 만들지 않게 최신 값만 들고 있는다.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  useEffect(() => {
    const back = backRef.current;
    const box = boxRef.current;
    if (!back || !box) return;
    let unmounting = false;
    const h = OneUI.bindModal(back, {
      dialog: box,
      input,
      // 언마운트로 닫힐 때는 부모가 이미 닫은 것이므로 onClose 를 다시 부르지 않는다
      onClose: () => {
        if (!unmounting) onCloseRef.current();
      },
      isDirty: isDirtyRef.current ? () => !!isDirtyRef.current?.() : undefined,
      // 열릴 때 첫 입력(없으면 첫 버튼)에 포커스
      initialFocus: () =>
        box.querySelector<HTMLElement>(
          'input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),button:not([disabled])'
        ),
    });
    handleRef.current = h;
    return () => {
      // 저장 성공 등으로 부모가 모달을 내렸을 때 — 층 목록에서 빼고 포커스를 되돌린다
      unmounting = true;
      h.close();
      if (handleRef.current === h) handleRef.current = null;
    };
  }, [input]);

  const requestClose = () => {
    void handleRef.current?.requestClose();
  };

  return (
    <ModalCloseContext.Provider value={requestClose}>
      <div className="modal-backdrop" ref={backRef}>
        <div className="modal" role="dialog" aria-modal="true" aria-label={title} ref={boxRef}>
          <h2 className="modal__title">{title}</h2>
          {desc && <p className="modal__desc">{desc}</p>}
          {children}
          {footer && <div className="modal__foot">{footer}</div>}
        </div>
      </div>
    </ModalCloseContext.Provider>
  );
}
